import { Injectable } from '@angular/core';
import { Observable, Subject, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { EncryptionService } from '../../../core/services/encryption.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SETTINGS } from '../../../core/config/common.settings';
import { LoadingService } from '../../../core/services/loading.service';
import { catchError } from 'rxjs/operators';
import { PrivilegeService } from '../../../core/services/privilege.service';
import {PosthogService} from "../../../core/services/posthog.service";

@Injectable()
export class AuthService {
  signupStatus = false;
  validationCheck: any;

  isConfirmed = true;
  unconfirmedUserId: any;
  unconfirmedUser: any;
  private loginStatus = new Subject();

  constructor(
    private http: HttpClient,
    private router: Router,
    private encryption: EncryptionService,
    private message: NzMessageService,
    private loadingService: LoadingService,
    private privilegeService: PrivilegeService, private ph: PosthogService,
  ) {}

  login(email: string, password: string): Promise<any> {
    this.loadingService.start();
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.BASE_API}/admin/login`, { email, password })
        .subscribe({
          next: (response: any) => {
            this.setSession(response);
            resolve(response);
          },
          error: (error: any) => {
            if (error.status === 403 || error === 403) {
              this.loginStatus.next({ isAuthenticated: false });
            }
            this.loadingService.stop();
            reject(error.error.message);
          },
        });
    });
  }

  private setSession(authResult: any): void {
    const { access_token, refresh_token, user } = authResult;
    this.setLoggedInUser(JSON.stringify(user));
    localStorage.setItem(
      SETTINGS.ACCESS_TOKEN,
      this.encryption.encrypt(access_token)
    );
    localStorage.setItem(
      SETTINGS.REFRESH_TOKEN,
      this.encryption.encrypt(refresh_token)
    );
    this.loginStatus.next({ isAuthenticated: true });
    this.privilegeService.setUserPrivileges(user.permissions);
    this.loadingService.stop();
  }

  logout(): void {
    this.loadingService.start();
    this.http.post(`${SETTINGS.BASE_API}/admin/logout`, {}).subscribe({
      next: () => {
        localStorage.clear();
        this.loadingService.stop();
        this.ph.reset();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        localStorage.clear();
        this.loadingService.stop();
        this.ph.reset();
        this.router.navigate(['/auth/login']);
      },
    });
  }

  public isLoggedIn(): any {
    if (
      localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null &&
      localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null
    ) {
      const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        // this.toasterService.showToaster('Invalid token', SETTINGS.TOASTER_MESSAGES.error);
        this.logout();
      }
    }
    return false;
  }

  getToken(): any {
    if (localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null) {
      const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);

      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        return '';
      }
    }
    return false;
  }

  getRefreshToken(): any {
    if (localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null) {
      const refreshTokenENC: any = localStorage.getItem(SETTINGS.REFRESH_TOKEN);

      try {
        return this.encryption.decrypt(refreshTokenENC);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  getLoggedInStatus(): Observable<any> {
    return this.loginStatus.asObservable();
  }

  setLoggedInUser(userStr: any): void {
    localStorage.setItem(
      SETTINGS.LOGGED_IN_USER,
      this.encryption.encrypt(userStr)
    );
  }

  getLoggedInUser(): any {
    const userStrEnc: any = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    return JSON.parse(this.encryption.decrypt(userStrEnc));
  }

  refreshToken(): Observable<any> {
    return this.http
      .post(`${SETTINGS.BASE_API}/admin/refresh-token`, {
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        tap((res: any) => {
          localStorage.setItem(
            SETTINGS.ACCESS_TOKEN,
            this.encryption.encrypt(res.access_token)
          );
        }),
        catchError((err, caught) => {
          this.loadingService.stop();
          localStorage.clear();
          this.router.navigate(['/auth/login']);
          return err;
        })
      );
  }

  isSuperAdmin(): any {
    return (this.getLoggedInUser().role.isSuperAdmin);
  }

  verifyRoles(rolesToBeVerified: string[]): boolean {
    const userStrEnc: any = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    const user = JSON.parse(this.encryption.decrypt(userStrEnc));
    return rolesToBeVerified.includes(user.role);
  }

  resendOTP(payload: any): void {
    this.loadingService.start();
    this.http.post(`${SETTINGS.BASE_API}/admin/resend-otp`, payload).subscribe(
      (response: any) => {
        this.unconfirmedUser = response;
        this.loadingService.stop();
        this.message.create(
          'success',
          'Please check the email and verify your account first.'
        );
      },
      (error: any) => {
        this.loadingService.stop();
        this.message.create('error', error.error.message);
      }
    );
  }

  otp(payload: any): void {
    this.loadingService.start();
    this.http
      .post(`${SETTINGS.BASE_API}/admin/confirmation`, payload)
      .subscribe(
        (response: any) => {
          this.message.create('success', 'Verification has been successful');
          this.isConfirmed = true;
          this.unconfirmedUser = undefined;
          this.unconfirmedUserId = undefined;
          this.router.navigate(['/auth/login']);
          this.loadingService.stop();
        },
        (error1: any) => {
          this.isConfirmed = false;
          this.message.create('error', error1.error.message);
          this.loadingService.stop();
          return error1;
        }
      );
  }
}
